import React, { useState } from 'react';
import {
  CandiBankEmptySearchIcons,
  ChartIcons,
  ChevronDownIcons,
  FilePlusIcons,
  LayoutGridIcons,
  ListIcons,
  RefreshListIcons,
  SearchIcons,
  UploadIcons,
} from '../../icons/icons';
import { MainLayout } from '../../ui';
import { AddToInviteListModal } from './Components/AddToInviteListModal';
import { AnalyticsDialog } from './Components/AnalyticsDialog';
import { CandidateCard } from './Components/CandidateCard';
import { CandidateProfileModal } from './Components/CandidateProfileModal';
import { CandidateTable } from './Components/CandidateTable';
import { FilterDrawer } from './Components/FilterDrawer';
import {
  CandidateTableSkeleton,
  SkeletonGrid,
} from './Components/SkeletonLoaders';
import { UseJdModal } from './Components/UseJdModal';
import { ViewInviteListModal } from './Components/ViewInviteListModal';
import { CandiBankUploadModal } from './Components/CandiBankUploadModal';
import { CandiBankUploadWidget } from './Components/CandiBankUploadWidget';
import { BankStatsBar } from './Components/BankStatsBar';
import { useCandiBankStats } from './hooks/useCandiBankStats';
import { toast } from '../../ui/toast';
import { formatResetDate } from '../../utils/types/credits_types';
import { mockCandidates } from './constants/mockData';
import { useCandidateExplorer } from './hooks/UseCandidateExplorer';
import { useCandiBankUpload } from './hooks/useCandiBankUpload';
import { useBankSearch } from './hooks/useBankSearch';
import { BankSearchTab } from './Components/BankSearch/BankSearchTab';

export const CandibankExplorerContent = () => {
  const {
    perPageDropdownRef,
    setShowPerPageDropdown,
    showPerPageDropdown,
    candidatesPerPage,
    setCandidatesPerPage,
    totalCandidates,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    setViewMode,
    viewMode,
    handleRefresh,
    showAnalyticsDialog,
    setShowAnalyticsDialog,
    setExtractedBehavioralScore,
    extractedBehavioralScore,
    setExtractedKeywords,
    extractedKeywords,
    setExtractedJobTitles,
    extractedJobTitles,
    setExtractedSkill,
    extractedSkill,
    setLoading,
    loading,
    candidates,
    setSearchObject,
    searchObject,
    getMatchColor,
    getStatusText,
    getStatusColor,
    handleCloseProfile,
    showProfileModal,
    selectedCandidate,
    availableLocations,
    handleClearFilters,
    showFilters,
    setShowFilters,
    availableSkills,
    handleFiltersChange,
    filters,
    setShowViewInviteListModal,
    showViewInviteListModal,
    selectedCandidates,
    selectedTransformedCandidates,
    candidateForInvite,
    showInviteModal,
    setShowInviteModal,
    totalPages,
    currentPage,
    handleOpenInviteModal,
    handleViewProfile,
    handleSelectAll,
    handleCandidateSelect,
    isLoading,
    getActiveFiltersCount,
    highScoreCandidates,
    handleBulkInvite,
    setSelectedCandidates,
    isRefreshing,
    showBulkActions,
    showUseJdModal,
    setShowUseJdModal,
    handleAutoFillSearch,
    handleAutoFillAnalytics,
    handleClearSearch,
    handleClearAnalytics,
  } = useCandidateExplorer();

  const bankStats = useCandiBankStats();
  const uploadState = useCandiBankUpload(
    () => {
      handleRefresh();
      bankStats.refresh();
    },
    {
      onCredits: bankStats.applyCredits,
      onBlocked: (credits) =>
        toast.error(
          credits
            ? `Monthly CV credit limit reached. 0 of ${credits.monthlyLimit} left, resets ${formatResetDate(credits)}.`
            : 'Monthly CV credit limit reached.',
          { position: 'bottom-center' },
        ),
    },
  );
  const bankSearch = useBankSearch();
  const [activeTab, setActiveTab] = useState<'pool' | 'search'>('pool');
  return (
    <div className='min-h-screen bg-[#fcfcfd]'>
      <div className='relative z-10 p-6 max-w-[1600px] mx-auto'>
        { /* Header Section */ }
        <div className='mb-6 flex items-end justify-between'>
          <div>
            <h1 className='text-xl font-bold text-slate-900 tracking-tight'>
              Candidate Explorer
            </h1>
            <p className='text-[13px] text-slate-500 font-medium'>
              Manage and discover talent with AI-powered matching
            </p>
          </div>
          <div
              className='relative flex items-center gap-2'
              ref={perPageDropdownRef}
          >
            <button
                onClick={() => setShowPerPageDropdown((v) => !v)}
                className='flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md border border-slate-200 shadow-sm hover:border-indigo-400 hover:bg-indigo-50 transition-all group'
            >
              <span className='text-[11px] font-bold text-slate-600 uppercase tracking-wider'>
                { totalCandidates } Candidates
              </span>
              <ChevronDownIcons
                  className={`w-3 h-3 text-slate-400 group-hover:text-indigo-500 transition-transform ${
                    showPerPageDropdown ? 'rotate-180' : ''
                  }`}
              />
            </button>
            { showPerPageDropdown && (
              <div className='absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden'>
                <div className='px-3 py-1.5 border-b border-slate-100'>
                  <span className='text-[10px] font-bold text-slate-400 uppercase tracking-wider'>
                    Per page
                  </span>
                </div>
                { [25, 50, 100].map((n) => (
                  <button
                      key={n}
                      onClick={() => {
                        setCandidatesPerPage(n);
                        setCurrentPage(1);
                        setShowPerPageDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-[12px] font-medium transition-colors ${
                        candidatesPerPage === n
                          ? 'bg-indigo-50 text-indigo-700 font-bold'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    { n } per page
                    { candidatesPerPage === n && (
                      <span className='float-right text-indigo-500'>✓</span>
                    ) }
                  </button>
                )) }
              </div>
            ) }
          </div>
        </div>

        {/* Bank totals + this month's CV credits */}
        <BankStatsBar stats={bankStats.stats} loading={bankStats.loading} />

        {/* Tab Switcher */}
        <div className='flex items-center gap-1 mb-6 bg-slate-100/80 p-1 rounded-lg border border-slate-200/50 w-fit'>
          <button
            onClick={() => setActiveTab('pool')}
            className={`px-4 py-2 text-[12px] font-bold rounded-md transition-all ${
              activeTab === 'pool'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Talent Pool
          </button>
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 text-[12px] font-bold rounded-md transition-all ${
              activeTab === 'search'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Advanced Search
          </button>
        </div>

        {activeTab === 'search' ? (
          <BankSearchTab state={bankSearch} />
        ) : (
        <>
        {/* Search and Filter Bar */}
        <div className='mb-6'>
          <div className='flex flex-col lg:flex-row gap-3 items-center justify-between'>
            <div className='flex-1 w-full max-w-2xl flex items-center gap-2'>
              <div className='relative group flex-1'>
                <SearchIcons className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 transition-colors group-focus-within:text-indigo-500' />
                <input
                    type='text'
                    placeholder='Search candidates by name, skills, or location...'
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className='w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm text-slate-700 placeholder-slate-400 shadow-sm'
                />
              </div>
              <button
                  type='button'
                  onClick={() => setShowUseJdModal(true)}
                  className='flex items-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all whitespace-nowrap'
              >
                <FilePlusIcons className='w-4 h-4 text-slate-500' />
                Use JD
              </button>
              <button
                  type='button'
                  onClick={() => uploadState.setShowUploadModal(true)}
                  disabled={bankStats.exhausted}
                  title={
                    bankStats.exhausted && bankStats.credits
                      ? `Monthly CV credit limit reached. Resets ${formatResetDate(bankStats.credits)}.`
                      : undefined
                  }
                  className='flex items-center gap-2 px-4 py-2.5 bg-emerald-600 border border-emerald-600 rounded-lg text-white text-xs font-bold hover:bg-emerald-700 shadow-sm shadow-emerald-200 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600'
              >
                <UploadIcons className='w-4 h-4 text-white' />
                Upload CVs
              </button>
            </div>

            <div className='flex items-center gap-2 w-full lg:w-auto'>
              <div className='flex items-center bg-slate-100/80 p-1 rounded-lg border border-slate-200/50'>
                <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1.5 text-[12px] font-bold rounded-md transition-all flex items-center gap-2 ${
                      viewMode === 'grid'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <LayoutGridIcons className='w-3.5 h-3.5' />
                  Grid
                </button>
                <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 text-[12px] font-bold rounded-md transition-all flex items-center gap-2 ${
                      viewMode === 'list'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <ListIcons className='w-3.5 h-3.5' />
                  List
                </button>
              </div>

              <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className='p-2 text-slate-400 hover:text-slate-600 active:scale-95 transition-all'
                  title='Refresh candidates'
              >
                <RefreshListIcons
                    className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                />
              </button>

              <div className='h-6 w-px bg-slate-200 mx-1 hidden lg:block'></div>
              { showBulkActions && (
                <button
                    onClick={() => setSelectedCandidates([])}
                    className='flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-slate-700 font-bold text-[12px] shadow-sm active:scale-[0.98]'
                >
                  Clear Selection
                </button>
              ) }
              <button
                  onClick={handleBulkInvite}
                  disabled={selectedCandidates.length === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-[12px] shadow-sm transition-all active:scale-[0.98] ${
                    selectedCandidates.length > 0
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-600'
                      : 'bg-white text-slate-400 border border-slate-200 cursor-not-allowed opacity-50'
                  }`}
              >
                <FilePlusIcons className='w-3.5 h-3.5' />
                Invite List
                { selectedCandidates.length > 0
                  ? ` (${selectedCandidates.length})`
                  : '' }
              </button>

              <button
                  onClick={() => setShowViewInviteListModal(true)}
                  className='flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-slate-700 font-bold text-[12px] shadow-sm active:scale-[0.98]'
              >
                <SearchIcons className='w-3.5 h-3.5 text-slate-500' />
                View Invite List
              </button>

              <button
                  onClick={() => setShowAnalyticsDialog(true)}
                  className='flex items-center gap-2 px-4 py-2 bg-slate-900 text-white font-bold text-[12px] rounded-lg hover:bg-slate-800 transition-all shadow-sm active:scale-[0.98]'
              >
                <ChartIcons className='w-3.5 h-3.5' />
                Analytics
              </button>
            </div>
          </div>

          <div className='flex items-center gap-4 mt-4'>
            <div className='flex items-center gap-2'>
              <div className='w-1.5 h-1.5 rounded-full bg-indigo-500'></div>
              <span className='text-[12px] text-slate-500 font-medium'>
                <span className='text-slate-900 font-bold'>
                  { selectedTransformedCandidates.length }
                </span>{ ' ' }
                results found
              </span>
            </div>
            { highScoreCandidates > 0 && (
              <div className='flex items-center gap-2'>
                <div className='w-1.5 h-1.5 rounded-full bg-emerald-500'></div>
                <span className='text-[12px] text-slate-500 font-medium'>
                  <span className='text-emerald-600 font-bold'>
                    { highScoreCandidates }
                  </span>{ ' ' }
                  high match
                </span>
              </div>
            ) }
          </div>
        </div>

        { /* Bulk Actions Bar */ }

        { /* Results Header */ }
        { !isLoading && selectedTransformedCandidates.length > 0 && (
          <div className='flex items-center justify-between mb-4 px-1'>
            <div className='flex items-center gap-3'>
              <h2 className='text-[14px] font-bold text-slate-800'>
                Talent Pool
              </h2>
              <div className='h-4 w-px bg-slate-200'></div>
              <button
                  onClick={handleSelectAll}
                  className='text-[12px] font-bold text-indigo-600 hover:text-indigo-700 transition-colors'
              >
                { selectedCandidates.length ===
                selectedTransformedCandidates.length
                  ? 'Deselect All'
                  : 'Select All' }
              </button>
            </div>
          </div>
        ) }

        { /* Candidates Content Area */ }
        <div className='relative min-h-[400px]'>
          { isLoading ? (
            viewMode === 'grid' ? (
              <SkeletonGrid count={candidatesPerPage} />
            ) : (
              <CandidateTableSkeleton />
            )
          ) : selectedTransformedCandidates.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-center select-none'>
              <div className='mb-6'>
                <CandiBankEmptySearchIcons className='mx-auto' />
              </div>
              { !searchQuery || getActiveFiltersCount() > 0 ? (
                <>
                  <h3 className='text-lg font-bold text-slate-800 mb-1'>
                    No results for your search
                  </h3>
                  <p className='text-sm text-slate-500 max-w-sm mb-5 leading-relaxed'>
                    We couldn&apos;t find any candidates matching{ ' ' }
                    { searchQuery && (
                      <span className='font-semibold text-indigo-600'>
                        &ldquo;{ searchQuery }&rdquo;
                      </span>
                    ) }
                    { searchQuery && getActiveFiltersCount() > 0 && ' with ' }
                    { getActiveFiltersCount() > 0 && (
                      <span className='font-semibold text-indigo-600'>
                        { getActiveFiltersCount() } active filter
                        { getActiveFiltersCount() !== 1 ? 's' : '' }
                      </span>
                    ) }
                    . Try with new search term or filters.
                  </p>
                </>
              ) : (
                <>
                  <h3 className='text-lg font-bold text-slate-800 mb-1'>
                    Your talent pool is empty
                  </h3>
                  <p className='text-sm text-slate-500 max-w-sm leading-relaxed'>
                    No candidates have been added to the database yet. Upload
                    CVs or invite candidates to get started.
                  </p>
                </>
              ) }
            </div>
          ) : (
            <>
              { /* Candidates Grid/List */ }
              <MainLayout isLoading={isLoading}>
                { viewMode === 'grid' ? (
                  <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'>
                    { selectedTransformedCandidates.map((candidate) => (
                      <CandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          getMatchColor={getMatchColor}
                          getStatusColor={getStatusColor}
                          getStatusText={getStatusText}
                          isSelected={selectedCandidates.includes(candidate.id)}
                          onSelect={() => handleCandidateSelect(candidate.id)}
                          onViewProfile={handleViewProfile}
                          onInvite={handleOpenInviteModal}
                      />
                    )) }
                  </div>
                ) : (
                  <CandidateTable
                      candidates={selectedTransformedCandidates}
                      getMatchColor={getMatchColor}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                      selectedCandidates={selectedCandidates}
                      onCandidateSelect={handleCandidateSelect}
                      onSelectAll={handleSelectAll}
                      onViewProfile={handleViewProfile}
                      onInvite={handleOpenInviteModal}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCandidates={totalCandidates}
                      candidatesPerPage={candidatesPerPage}
                      onPageChange={setCurrentPage}
                  />
                ) }
              </MainLayout>
            </>
          ) }

          <AddToInviteListModal
              isOpen={showInviteModal}
              onClose={() => setShowInviteModal(false)}
              candidateIds={
                candidateForInvite ? [candidateForInvite.id] : selectedCandidates
              }
              candidateName={candidateForInvite?.name}
              candidateNames={
                candidateForInvite
                  ? undefined
                  : selectedTransformedCandidates
                    .filter((c) => selectedCandidates.includes(c.id))
                    .map((c) => c.name)
              }
          />
          <ViewInviteListModal
              isOpen={showViewInviteListModal}
              onClose={() => setShowViewInviteListModal(false)}
          />
        </div>

        { /* Filter Drawer */ }
        <FilterDrawer
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            availableSkills={availableSkills}
            availableLocations={availableLocations}
        />

        { /* Candidate Profile Modal */ }
        <CandidateProfileModal
            candidate={selectedCandidate}
            isOpen={showProfileModal}
            onClose={handleCloseProfile}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
            getMatchColor={getMatchColor}
            onInvite={handleOpenInviteModal}
        />

        { /* Analytics Dialog */ }
        { showAnalyticsDialog && (
          <AnalyticsDialog
              isOpen={showAnalyticsDialog}
              onClose={() => setShowAnalyticsDialog(false)}
              totalCandidates={mockCandidates.length}
              searchObject={searchObject}
              setSearchObject={setSearchObject}
              candidates={candidates}
              loading={loading}
              setLoading={setLoading}
              extractedSkill={extractedSkill}
              setExtractedSkill={setExtractedSkill}
              extractedJobTitles={extractedJobTitles}
              setExtractedJobTitles={setExtractedJobTitles}
              extractedKeywords={extractedKeywords}
              setExtractedKeywords={setExtractedKeywords}
              extractedBehavioralScore={extractedBehavioralScore}
              setExtractedBehavioralScore={setExtractedBehavioralScore}
          />
        ) }

        { /* Use JD Modal */ }
        <UseJdModal
            isOpen={showUseJdModal}
            onClose={() => setShowUseJdModal(false)}
            onCreateFromFile={async (file) => {
              await bankSearch.createFromFile(file);
              setActiveTab('search');
            }}
            onCreateFromText={async (text) => {
              await bankSearch.createFromText(text);
              setActiveTab('search');
            }}
            onCreateFromOpening={async (openingId) => {
              await bankSearch.createFromOpening(openingId);
              setActiveTab('search');
            }}
            isCreating={bankSearch.createLoading}
            onAutoFillSearch={handleAutoFillSearch}
            onAutoFillAnalytics={handleAutoFillAnalytics}
            onClearSearch={handleClearSearch}
            onClearAnalytics={handleClearAnalytics}
        />

        { /* Candi Bank Bulk CV Upload Modal & Floating Google Drive Widget */ }
        <CandiBankUploadModal
            isOpen={uploadState.showUploadModal}
            onClose={() => uploadState.setShowUploadModal(false)}
            onFilesSelected={uploadState.addFilesToQueue}
            credits={bankStats.credits}
        />

        <CandiBankUploadWidget
            items={uploadState.items}
            isOpen={uploadState.showWidget}
            isMinimized={uploadState.isWidgetMinimized}
            onMinimizeToggle={() => uploadState.setIsWidgetMinimized((prev) => !prev)}
            onClose={() => uploadState.setShowWidget(false)}
            onRetry={uploadState.retryItem}
            onRemove={uploadState.removeItem}
            onClearCompleted={uploadState.clearCompleted}
            formatFileSize={uploadState.formatFileSize}
            overallProgress={uploadState.overallProgress}
            completedCount={uploadState.completedCount}
            duplicateCount={uploadState.duplicateCount}
            totalCount={uploadState.totalCount}
            isUploading={uploadState.isUploading}
        />

        <div className='flex items-center gap-2 text-sm text-slate-600'></div>
        </>
        )}
      </div>
    </div>
  );
};
